import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config/api';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ConfirmGuarantor = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const token = query.get('token');
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error' | 'already-processed'
  const [message, setMessage] = useState('');
  const [previousDecision, setPreviousDecision] = useState(null);

  useEffect(() => {
    const confirm = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Missing token. The confirmation link appears invalid.');
        return;
      }

      try {
        const resp = await axios.get(`${API_URL}/api/guarantors/confirm?token=${encodeURIComponent(token)}`);
        
        if (resp.data?.alreadyProcessed) {
          setStatus('already-processed');
          setMessage(resp.data?.message || 'You have already responded to this request.');
          setPreviousDecision(resp.data?.previousDecision);
        } else {
          setStatus('success');
          setMessage(resp.data?.message || 'Thank you — your guarantee has been recorded.');
        }
      } catch (err) {
        console.error('Confirm error', err?.response || err);
        const serverMsg = err?.response?.data?.message || err?.message;
        setStatus('error');
        setMessage(serverMsg || 'Failed to confirm guarantee. The link may have expired or is invalid.');
      }
    };

    confirm();
  }, [token]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '50px',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        maxWidth: '550px',
        width: '100%',
        textAlign: 'center'
      }}>
        {status === 'loading' && (
          <>
            <div style={{ fontSize: '64px', marginBottom: '20px', animation: 'spin 2s linear infinite' }}>⏳</div>
            <h2 style={{ color: '#333', fontSize: '28px', marginBottom: '10px' }}>Processing Your Response</h2>
            <p style={{ color: '#666', fontSize: '16px' }}>Please wait while we verify your guarantee confirmation...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{ 
              fontSize: '72px', 
              marginBottom: '20px', 
              color: '#10b981',
              background: '#d1fae5',
              borderRadius: '50%',
              width: '120px',
              height: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>✓</div>
            <h2 style={{ color: '#10b981', fontSize: '32px', marginBottom: '15px', fontWeight: 'bold' }}>Guarantee Confirmed!</h2>
            <p style={{ color: '#333', fontSize: '16px', marginBottom: '20px', lineHeight: '1.6' }}>
              Thank you for accepting to be a guarantor. Your confirmation has been successfully recorded.
            </p>
            <div style={{
              background: '#f0f9ff',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              padding: '20px',
              marginTop: '25px',
              textAlign: 'left'
            }}>
              <h3 style={{ color: '#2563eb', fontSize: '18px', marginBottom: '10px' }}>What happens next?</h3>
              <ul style={{ color: '#666', fontSize: '14px', lineHeight: '1.8', paddingLeft: '20px', margin: 0 }}>
                <li>The loan applicant will be notified of your confirmation</li>
                <li>The application will proceed to admin review</li>
                <li>You will be contacted if any additional information is needed</li>
              </ul>
            </div>
            <button 
              onClick={() => window.close()} 
              style={{
                marginTop: '30px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                padding: '14px 32px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'background 0.3s'
              }}
              onMouseOver={(e) => e.target.style.background = '#1d4ed8'}
              onMouseOut={(e) => e.target.style.background = '#2563eb'}
            >
              Close Window
            </button>
          </>
        )}

        {status === 'already-processed' && (
          <>
            <div style={{ 
              fontSize: '72px', 
              marginBottom: '20px', 
              color: '#6366f1',
              background: '#e0e7ff',
              borderRadius: '50%',
              width: '120px',
              height: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>ℹ</div>
            <h2 style={{ color: '#6366f1', fontSize: '32px', marginBottom: '15px', fontWeight: 'bold' }}>Decision Already Recorded</h2>
            <p style={{ color: '#333', fontSize: '16px', marginBottom: '20px', lineHeight: '1.6' }}>
              {message}
            </p>
            <div style={{
              background: previousDecision === 'confirmed' ? '#d1fae5' : '#fef3c7',
              border: previousDecision === 'confirmed' ? '1px solid #86efac' : '1px solid #fcd34d',
              borderRadius: '8px',
              padding: '20px',
              marginTop: '25px'
            }}>
              <h3 style={{ 
                color: previousDecision === 'confirmed' ? '#10b981' : '#f59e0b', 
                fontSize: '20px', 
                marginBottom: '10px',
                fontWeight: 'bold'
              }}>
                Your Previous Decision: {previousDecision === 'confirmed' ? 'ACCEPTED ✓' : 'DECLINED ✗'}
              </h3>
              <p style={{ color: '#666', fontSize: '14px', margin: 0, lineHeight: '1.6' }}>
                {previousDecision === 'confirmed' 
                  ? 'You have already accepted to be a guarantor for this loan application.'
                  : 'You have already declined to be a guarantor for this loan application.'}
              </p>
            </div>
            <button 
              onClick={() => window.close()} 
              style={{
                marginTop: '30px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                padding: '14px 32px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'background 0.3s'
              }}
              onMouseOver={(e) => e.target.style.background = '#1d4ed8'}
              onMouseOut={(e) => e.target.style.background = '#2563eb'}
            >
              Close Window
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{ 
              fontSize: '72px', 
              marginBottom: '20px', 
              color: '#ef4444',
              background: '#fee2e2',
              borderRadius: '50%',
              width: '120px',
              height: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>✗</div>
            <h2 style={{ color: '#ef4444', fontSize: '28px', marginBottom: '15px' }}>Confirmation Failed</h2>
            <p style={{ color: '#666', fontSize: '16px', marginBottom: '20px', lineHeight: '1.6' }}>{message}</p>
            <div style={{
              background: '#fef3c7',
              border: '1px solid #fcd34d',
              borderRadius: '8px',
              padding: '20px',
              marginTop: '20px'
            }}>
              <p style={{ color: '#92400e', fontSize: '14px', margin: 0, lineHeight: '1.6' }}>
                <strong>Need Help?</strong><br/>
                If you believe this is an error or need assistance, please contact SmartLend Nigeria support at <strong>support@smartlend.ng</strong>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmGuarantor;
